import React from 'react';
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { DatePicker } from '../ui/DatePicker';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  Users, Building2, ChevronDown, Search, RefreshCw,
  ArrowRight, Filter, ChevronLeft, ChevronRight, Calculator,
} from 'lucide-react';
import { CalendarDays } from 'lucide-react';

const SCOPE_OPTIONS = [
  { value: 'all',        labelKey: 'attComp.filters.scopeAll',    icon: Users },
  { value: 'department', labelKey: 'attComp.filters.scopeDept',   icon: Building2 },
  { value: 'manual',     labelKey: 'attComp.filters.scopeManual', icon: ChevronDown },
];

const PERIOD_OPTIONS = [
  { value: 'today',  labelKey: 'attComp.filters.periodToday' },
  { value: 'week',   labelKey: 'attComp.filters.periodWeek' },
  { value: 'month',  labelKey: 'attComp.filters.periodMonth' },
  { value: 'custom', labelKey: 'attComp.filters.periodCustom' },
];

const PointagesFiltersCard = ({
  scope, setScope,
  selectedDeptId, setSelectedDeptId,
  departments,
  empPopoverOpen, setEmpPopoverOpen,
  selectedEmployeeNames,
  filteredEmployeesForPopover,
  selectedEmpIds, setSelectedEmpIds,
  empSearchQuery, setEmpSearchQuery,
  toggleEmployee,
  period, handlePeriodChange,
  dateFrom, setDateFrom,
  dateTo, setDateTo,
  dateRangeLabel, dayCount,
  handleNavigate,
  loadingPreview, previewList,
  canCalculate, calculating,
  handleCalculate,
  resetResults,
}) => {
  const { t } = useTranslation();
  return (
  <Card>
    <CardHeader className="pb-3">
      <CardTitle className="flex items-center gap-2 text-base">
        <Filter className="h-4 w-4" />
        {t("attComp.filters.paramsTitle")}
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-6">

      {/* Périmètre employés */}
      <div className="space-y-3">
        <Label className="text-sm font-semibold">{t("attComp.filters.empScope")}</Label>
        <div className="flex flex-wrap gap-3">
          {SCOPE_OPTIONS.map(({ value, labelKey, icon: Icon }) => (
            <button
              key={value}
              onClick={() => { setScope(value); resetResults(); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors
                ${scope === value
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border bg-card text-foreground hover:bg-accent'}`}
            >
              <Icon className="h-4 w-4" />
              {t(labelKey)}
            </button>
          ))}
        </div>

        {scope === 'department' && (
          <Select value={selectedDeptId} onValueChange={v => { setSelectedDeptId(v); resetResults(); }}>
            <SelectTrigger className="w-72">
              <SelectValue placeholder={t("attComp.filters.chooseDept")} />
            </SelectTrigger>
            <SelectContent>
              {departments.map(d => (
                <SelectItem key={d.id} value={String(d.id)}>{d.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {scope === 'manual' && (
          <div className="flex items-center gap-2">
            <Popover open={empPopoverOpen} onOpenChange={setEmpPopoverOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-72 justify-between">
                  <span className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    {selectedEmployeeNames || t('attComp.filters.chooseEmployees')}
                  </span>
                  <ChevronDown className="h-4 w-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0" align="start">
                <div className="p-2 border-b">
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder={t("attComp.searchPlaceholder")}
                      className="pl-8 h-9"
                      value={empSearchQuery}
                      onChange={e => setEmpSearchQuery(e.target.value)}
                    />
                  </div>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {filteredEmployeesForPopover.map(emp => (
                    <label key={emp.id} className="flex items-center gap-2 px-3 py-2 hover:bg-accent cursor-pointer">
                      <Checkbox
                        checked={selectedEmpIds.includes(emp.id)}
                        onCheckedChange={() => toggleEmployee(emp.id)}
                      />
                      <span className="text-sm">
                        <span className="font-medium">{emp.last_name} {emp.first_name}</span>
                        <span className="text-muted-foreground ml-2 text-xs">{emp.registration_number}</span>
                      </span>
                    </label>
                  ))}
                </div>
                {selectedEmpIds.length > 0 && (
                  <div className="p-2 border-t flex justify-between items-center">
                    <span className="text-xs text-muted-foreground">{t("attComp.filters.selectedN", { count: selectedEmpIds.length })}</span>
                    <Button variant="ghost" size="sm" onClick={() => { setSelectedEmpIds([]); resetResults(); }}>
                      {t("attComp.filters.clear")}
                    </Button>
                  </div>
                )}
              </PopoverContent>
            </Popover>
            {selectedEmpIds.length > 0 && <Badge variant="secondary">{selectedEmpIds.length}</Badge>}
          </div>
        )}
      </div>

      {/* Période */}
      <div className="space-y-3">
        <Label className="text-sm font-semibold">{t("attComp.filters.period")}</Label>
        <div className="flex flex-wrap gap-3">
          {PERIOD_OPTIONS.map(({ value, labelKey }) => (
            <button
              key={value}
              onClick={() => handlePeriodChange(value)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors
                ${period === value
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border bg-card text-foreground hover:bg-accent'}`}
            >
              <CalendarDays className="h-4 w-4" />
              {t(labelKey)}
            </button>
          ))}
        </div>

        {period === 'custom' && (
          <div className="flex items-center gap-3 mt-2">
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">{t("attComp.dialogs.fromLabel")}</Label>
              <DatePicker value={dateFrom} maxDate={dateTo} onChange={(v) => { setDateFrom(v); resetResults(); }} className="w-44" />
            </div>
            <ArrowRight className="h-4 w-4 text-muted-foreground mt-5" />
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">{t("attComp.dialogs.toLabel")}</Label>
              <DatePicker value={dateTo} minDate={dateFrom} onChange={(v) => { setDateTo(v); resetResults(); }} className="w-44" />
            </div>
          </div>
        )}

        {dateFrom && dateTo && (
          <div className="flex items-center gap-2">
            <button onClick={() => handleNavigate(-1)} className="p-1 rounded hover:bg-muted text-muted-foreground transition-colors">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <p className="text-xs text-muted-foreground min-w-0">
              {dateRangeLabel} · {t("attComp.filters.daysCount", { count: dayCount })}
            </p>
            <button onClick={() => handleNavigate(1)} className="p-1 rounded hover:bg-muted text-muted-foreground transition-colors">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {/* Calculer button */}
      <div className="pt-2 border-t flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {loadingPreview ? (
            <span className="flex items-center gap-1"><RefreshCw className="h-3 w-3 animate-spin" /> {t("common.loading")}</span>
          ) : previewList.length > 0 ? (
            <span>{t("attComp.filters.empDays", { emp: previewList.length, days: dayCount })}</span>
          ) : scope === 'manual' && !selectedEmpIds.length ? (
            <span className="text-orange-600">{t("attComp.filters.selectAtLeastOne")}</span>
          ) : (
            <span>{t("attComp.filters.noEmployee")}</span>
          )}
        </div>
        <Button size="lg" onClick={handleCalculate}
          disabled={!canCalculate || calculating || previewList.length === 0}
          className="gap-2 px-8">
          {calculating
            ? <><RefreshCw className="h-4 w-4 animate-spin" /> {t("attComp.filters.calculating")}</>
            : <><Calculator className="h-4 w-4" /> {t("attComp.filters.calculate")}</>}
        </Button>
      </div>
    </CardContent>
  </Card>
  );
};

export default PointagesFiltersCard;
